import React from 'react';
import styled, { keyframes } from 'styled-components';
import { colors, spacing, radius } from '../../tokens';

interface SkeletonProps {
  width?: string;
  height?: string;
  borderRadius?: string;
  className?: string;
  style?: React.CSSProperties;
}

interface LoadingStateProps {
  variant?: 'card' | 'list' | 'table' | 'text' | 'custom';
  count?: number;
  children?: React.ReactNode;
}

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const SkeletonBase = styled.div<SkeletonProps>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '20px'};
  border-radius: ${props => props.borderRadius || radius.sm};
  background: linear-gradient(
    90deg,
    ${colors.background.tertiary} 25%,
    ${colors.background.secondary} 50%,
    ${colors.background.tertiary} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export const Skeleton: React.FC<SkeletonProps> = (props) => {
  return <SkeletonBase {...props} />;
};

// Preset skeleton components
const CardSkeleton = styled.div`
  border: 1px solid ${colors.border.primary};
  border-radius: ${radius.lg};
  padding: ${spacing[6]};
  background: ${colors.background.primary};
`;

const CardSkeletonHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[4]};
`;

const CardSkeletonContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const ListItemSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[4]};
  border-bottom: 1px solid ${colors.border.secondary};
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableRowSkeleton = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr;
  gap: ${spacing[4]};
  padding: ${spacing[4]};
  border-bottom: 1px solid ${colors.border.secondary};
`;

const TextBlockSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

// Loading state component
export const LoadingState: React.FC<LoadingStateProps> = ({
  variant = 'card',
  count = 1,
  children,
}) => {
  if (children) {
    return <>{children}</>;
  }

  const renderSkeleton = () => {
    switch (variant) {
      case 'card':
        return (
          <CardSkeleton>
            <CardSkeletonHeader>
              <Skeleton width="48px" height="48px" borderRadius={radius.full} />
              <div style={{ flex: 1 }}>
                <Skeleton width="60%" height="24px" />
                <Skeleton width="40%" height="16px" style={{ marginTop: spacing[2] }} />
              </div>
            </CardSkeletonHeader>
            <CardSkeletonContent>
              <Skeleton width="100%" height="16px" />
              <Skeleton width="90%" height="16px" />
              <Skeleton width="70%" height="16px" />
            </CardSkeletonContent>
          </CardSkeleton>
        );

      case 'list':
        return (
          <ListItemSkeleton>
            <Skeleton width="40px" height="40px" borderRadius={radius.full} />
            <div style={{ flex: 1 }}>
              <Skeleton width="50%" height="20px" />
              <Skeleton width="30%" height="16px" style={{ marginTop: spacing[1] }} />
            </div>
            <Skeleton width="80px" height="32px" />
          </ListItemSkeleton>
        );

      case 'table':
        return (
          <TableRowSkeleton>
            <Skeleton width="100%" height="20px" />
            <Skeleton width="100%" height="20px" />
            <Skeleton width="100%" height="20px" />
            <Skeleton width="100%" height="20px" />
          </TableRowSkeleton>
        );

      case 'text':
        return (
          <TextBlockSkeleton>
            <Skeleton width="100%" height="20px" />
            <Skeleton width="95%" height="20px" />
            <Skeleton width="80%" height="20px" />
          </TextBlockSkeleton>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} style={{ marginBottom: index < count - 1 ? spacing[4] : 0 }}>
          {renderSkeleton()}
        </div>
      ))}
    </>
  );
}; 